import React from 'react';
import SupplyChart from "./SupplyChart";

const SupplyRequirementsPanel = ({
	                                 requirements,
	                                 sortConfig,
	                                 setSortConfig,
	                                 searchTerm,
	                                 setSearchTerm,
	                                 loading,
	                                 error,
	                                 showForecastSettings,
	                                 setShowForecastSettings,
                                 }) => {

	const requestSort = (key) => {
		let direction = 'ascending';
		if (sortConfig.key === key && sortConfig.direction === 'ascending') {
			direction = 'descending';
		}
		setSortConfig({ key, direction });
	};

	const getSortedRequirements = () => {
		if (!requirements.length) return [];

		let sortableItems = [...requirements];
		if (searchTerm.trim()) {
			const normalizedSearchTerm = searchTerm.toLowerCase().trim();
			sortableItems = sortableItems.filter(item => {
				return (
					item.part_type_name.toLowerCase().includes(normalizedSearchTerm) ||
					item.vendor?.toLowerCase().includes(normalizedSearchTerm) ||
					item.type?.toLowerCase().includes(normalizedSearchTerm)
				);
			});
		}

		if (sortConfig.key) {
			sortableItems.sort((a, b) => {
				let aValue = a[sortConfig.key];
				let bValue = b[sortConfig.key];
				if (typeof aValue === 'string') {
					aValue = aValue.toLowerCase();
					bValue = bValue.toLowerCase();
				}
				if (aValue < bValue) {
					return sortConfig.direction === 'ascending' ? -1 : 1;
				}
				if (aValue > bValue) {
					return sortConfig.direction === 'ascending' ? 1 : -1;
				}
				return 0;
			});
		}

		return sortableItems;
	};

	const getSortIndicator = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
		}
		return '';
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const clearSearch = () => {
		setSearchTerm('');
	};

	const sortedRequirements = getSortedRequirements();

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	{/* Initial State - No Forecast Generated */
	}
	if (!loading && !error && requirements.length === 0 && !showForecastSettings) {
		return (
			<div className="bg-white rounded-lg shadow p-8 text-center">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
				</svg>
				<h3 className="text-lg font-medium text-gray-900 mb-2">No Forecast Data Available</h3>
				<p className="text-gray-500 mb-4">Click the 'Forecast Settings' button to set parameters and generate a supply forecast.</p>
				<button
					onClick={() => setShowForecastSettings(true)}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
				>
					Open Forecast Settings
				</button>
			</div>
		)
	}

	if (!requirements.length) {
		return (
			<div className="p-6 text-center">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002 2h2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002 2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2m0 0"/>
				</svg>
				<h3 className="text-lg font-medium text-gray-900 mb-2">No Supply Requirements</h3>
				<p className="text-gray-500">Generate a forecast to view supply requirements.</p>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="mb-6">
				<div className="relative">
					<input
						type="text"
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pl-10 sm:pr-10"
						placeholder="Search by part type, vendor, or type..."
						value={searchTerm}
						onChange={handleSearchChange}
					/>
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
						</svg>
					</div>
					{searchTerm && (
						<button
							className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
							onClick={clearSearch}
						>
							<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path stroke="round" strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
							</svg>
						</button>
					)}
				</div>
				{searchTerm && (
					<div className="mt-2 text-sm text-gray-500">
						Showing {sortedRequirements.length} of {requirements.length} part requirements
					</div>
				)}
			</div>

			<div className="mb-6">
				<SupplyChart/>
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
					<tr>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('part_type_name')}
						>
							Part Type{getSortIndicator('part_type_name')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('vendor')}
						>
							Vendor{getSortIndicator('vendor')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('type')}
						>
							Type{getSortIndicator('type')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('dimension')}
						>
							Dimension{getSortIndicator('dimension')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('current_stock')}
						>
							In Stock{getSortIndicator('current_stock')}
						</th>
						<th
							scope="col"
							className="px-2 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('total_quantity_needed')}
						>
							Quantity Needed
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('units_to_order')}
						>
							Units to Order{getSortIndicator('units_to_order')}
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('vendor_price')}
						>
							Unit Price
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
							onClick={() => requestSort('estimated_cost')}
						>
							Estimated Cost
						</th>
					</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
					{sortedRequirements.map((item) => (
						<tr key={item.part_type_id} className="hover:bg-gray-50">
							<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
								{item.part_type_name}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{item.vendor || 'N/A'}</td>
							<td className="px-6 py-4 whitespace-sm text-gray-500">
								{item.type || 'N/A'}</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{item.dimension} {item.unit_type}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{parseFloat(item.current_stock).toFixed(0)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{parseFloat(item.total_quantity_needed).toFixed(0)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
								{item.units_to_order}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.vendor_price || 0).toFixed(2)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
								${parseFloat(item.estimated_cost || 0).toFixed(2)}
							</td>
						</tr>
					))}
					</tbody>
					<tfoot className="bg-gray-50">
					<tr>
						<td colSpan="8" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
							Total Estimated Cost:
						</td>
						<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
							${sortedRequirements.reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0).toFixed(2)}
						</td>
					</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};

export default SupplyRequirementsPanel;
